import { EnumBuilder } from '@mntn-dev/utility-types'
import { z } from 'zod'
import { ModelFilterSchema } from '../model-filter-schema.ts'
import {
  PackagePayoutSchema,
  PackageTranslationsSchema,
} from './package.models.ts'
import {
  CompanyNameSchema,
  CostMarginPercentSchema,
  DateSchema,
  DescriptionSchema,
  DollarsSchema,
  NameSchema,
  NonEmptyStringSchema,
  TimestampSchema,
} from './property.models.ts'
import { ReviewRoundCountSchema } from './review.models.ts'
import {
  FileIdSchema,
  PackageIdSchema,
  ProjectIdSchema,
  ProjectUrnSchema,
  TeamIdSchema,
  UserIdSchema,
} from './unique-id.models.ts'

/**
 * ProjectStatus
 */
export const [ProjectStatuses, ProjectStatusSchema, ProjectStatusEnum] =
  EnumBuilder(
    'draft',
    'archived',
    'pricing',
    'priced',
    'submitted',
    'bidding_open',
    'bidding_closed',
    'awarded',
    'offered',
    'accepted',
    'matched',
    'pre_production',
    'pre_production_complete',
    'production',
    'post_production',
    'post_production_complete',
    'processing_final_files',
    'complete'
  )
export type ProjectStatus = z.infer<typeof ProjectStatusSchema>

export const AllExceptArchivedProjectStatuses = ProjectStatuses.filter(
  (status) => status !== 'archived'
)

export const InProgressProjectStatuses: Array<ProjectStatus> = [
  ProjectStatusEnum.pre_production,
  ProjectStatusEnum.pre_production_complete,
  ProjectStatusEnum.production,
  ProjectStatusEnum.post_production,
  ProjectStatusEnum.post_production_complete,
]

export const ProjectSubmittedStatusSchema = ProjectStatusSchema.extract([
  'submitted',
  'pricing',
])
export const ProjectSubmittedStatuses = ProjectSubmittedStatusSchema.options
export type ProjectSubmittedStatus = z.infer<
  typeof ProjectSubmittedStatusSchema
>

/**
 * ProjectDomainSelectModel
 */
export const ProjectDomainSelectModelSchema = z.object({
  projectId: ProjectIdSchema,
  projectUrn: ProjectUrnSchema,
  name: NameSchema,
  description: DescriptionSchema.optional(),
  dueDate: DateSchema.optional(),
  ownerId: UserIdSchema,
  brandTeamId: TeamIdSchema,
  acceptorId: UserIdSchema.optional(),
  agencyTeamId: TeamIdSchema.optional(),
  status: ProjectStatusSchema,
  thumbnailFileId: FileIdSchema.optional(),
  package: z.object({
    packageId: PackageIdSchema,
    name: NameSchema,
    description: DescriptionSchema,
    translations: PackageTranslationsSchema.optional(),
    cost: PackagePayoutSchema,
    costPlusMargin: DollarsSchema(),
  }),
  costMarginPercent: CostMarginPercentSchema,
  preProductionReviewRounds: ReviewRoundCountSchema.optional(),
  postProductionReviewRounds: ReviewRoundCountSchema.optional(),
  biddingCloseDate: DateSchema.optional(),
})

export type ProjectDomainSelectModel = z.infer<
  typeof ProjectDomainSelectModelSchema
>

export function isProjectWithStatus<
  Project extends ProjectDomainSelectModel,
  Status extends ProjectStatus,
>(project: Project, status: Status): project is Project & { status: Status } {
  return project.status === status
}

/**
 * ProjectViewDomainSelectModelSchema
 */
export const ProjectViewDomainSelectModelSchema =
  ProjectDomainSelectModelSchema.extend({
    lastTouchedTimestamp: TimestampSchema,
    brandCompanyName: CompanyNameSchema,
    cost: DollarsSchema(),
    costPlusMargin: DollarsSchema(),
  })

export type ProjectViewDomainSelectModel = z.infer<
  typeof ProjectViewDomainSelectModelSchema
>

/**
 * ProjectDomainInsertModel
 */
export const ProjectDomainInsertModelSchema =
  ProjectDomainSelectModelSchema.omit({
    projectUrn: true,
    thumbnailFileId: true,
  })
    .partial({
      projectId: true,
    })
    .extend({
      costMarginPercent: CostMarginPercentSchema,
      package: ProjectDomainSelectModelSchema.shape.package.omit({
        costPlusMargin: true,
      }),
    })

export type ProjectDomainInsertModel = z.infer<
  typeof ProjectDomainInsertModelSchema
>

/**
 * ProjectDomainUpdateModel
 */
export const ProjectDomainUpdateModelSchema =
  ProjectDomainSelectModelSchema.omit({
    projectId: true,
    projectUrn: true,
    package: true,
    costMarginPercent: true,
  })
    .extend({
      dueDate: ProjectDomainSelectModelSchema.shape.dueDate.nullable(),
      description: ProjectDomainSelectModelSchema.shape.description.nullable(),
      thumbnailFileId:
        ProjectDomainSelectModelSchema.shape.thumbnailFileId.nullable(),
      biddingCloseDate:
        ProjectDomainSelectModelSchema.shape.biddingCloseDate.nullable(),
    })
    .partial()

export type ProjectDomainUpdateModel = z.infer<
  typeof ProjectDomainUpdateModelSchema
>

/**
 * ProjectDomainSelectModelFilter
 */
export const ProjectDomainSelectModelFiltersSchema = z
  .object({
    projectId: ModelFilterSchema(ProjectIdSchema),
    projectUrn: ModelFilterSchema(ProjectUrnSchema),
    ownerId: ModelFilterSchema(UserIdSchema),
    brandTeamIds: ModelFilterSchema(TeamIdSchema),
    acceptorId: ModelFilterSchema(UserIdSchema),
    agencyTeamIds: ModelFilterSchema(TeamIdSchema),
    status: ModelFilterSchema(ProjectStatusSchema),
    search: NonEmptyStringSchema(),
  })
  .partial()

export type ProjectDomainSelectModelFilters = z.infer<
  typeof ProjectDomainSelectModelFiltersSchema
>
